import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Play } from "lucide-react";
import { HERO } from "@/constants/testIds";

// Vault product demo — Cloudflare Stream (BBV pattern, ref: ~/code/bbv/momentax HeroSection.tsx).
// When the demo is uploaded to CF Stream, paste the iframe URL here:
//   https://customer-<acct>.cloudflarestream.com/<videoId>/iframe
//     ?autoplay=true&muted=true&loop=true&preload=auto
//     &poster=<encodedPosterUrl>
// Until a URL is set, the stage shows a placeholder instead of a broken player.
const VAULT_DEMO_STREAM_URL = null;

const BENEFITS = [
  {
    bold: "Take on more cases",
    rest: "— without rebuilding the team behind each one.",
  },
  {
    bold: "Reopen the marketing budget",
    rest: "— and serve the demand it creates.",
  },
  {
    bold: "Chase the goals you'd quietly shelved",
    rest: "— now with a back office you can trust.",
  },
];

export default function HeroSideStage() {
  const onBook = (e) => {
    e.preventDefault();
    document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" });
  };
  const onHow = (e) => {
    e.preventDefault();
    document.querySelector("#whatwedo")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative pt-24 md:pt-28 pb-16 md:pb-20 bg-[#ffffff] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-6 right-6 h-px bg-black/8" />
        <div className="absolute bottom-6 left-6 right-6 h-px bg-black/8" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* LEFT — Copy */}
          <div className="lg:col-span-7 xl:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-mono text-[12px] uppercase tracking-[0.28em] text-black/65 mb-8 flex items-center gap-3"
            >
              <span className="eyebrow-bar" aria-hidden="true" />
              For Healthcare, Legal &amp; Finance Operators
            </motion.div>

            <motion.h1
              data-testid={HERO.stateHeadline(0)}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.2vw] xl:text-[4.6vw] leading-[0.98] tracking-tightest text-[#0a0a0a]"
            >
              Trained people,
              <span className="block italic font-light text-black/60 mt-2">
                augmented by AI.
              </span>
            </motion.h1>

            <motion.p
              data-testid={HERO.stateSub(0)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-8 font-sans text-black/75 text-base md:text-lg leading-relaxed max-w-xl"
            >
              A back office you can grow behind &mdash; built for healthcare,
              legal, and finance operators who can&rsquo;t afford to slow down.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 space-y-3 max-w-xl"
            >
              {BENEFITS.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 font-sans text-black/80 text-base md:text-lg leading-relaxed"
                >
                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.8}
                    className="mt-1 shrink-0 text-[#0a0a0a]"
                    aria-hidden="true"
                  />
                  <span>
                    <strong className="font-semibold text-[#0a0a0a]">
                      {b.bold}
                    </strong>{" "}
                    {b.rest}
                  </span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5"
            >
              <a
                href="#book"
                onClick={onBook}
                data-testid={HERO.ctaPrimary}
                className="btn-primary"
              >
                Book My Intro Call
                <ArrowRight size={14} />
              </a>
              <a
                href="#whatwedo"
                onClick={onHow}
                data-testid={HERO.ctaSecondary}
                className="font-mono text-[12px] uppercase tracking-[0.22em] text-black/75 hover:text-black transition-colors inline-flex items-center gap-2 group"
              >
                See how it works
                <ArrowRight
                  size={13}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </motion.div>
          </div>

          {/* RIGHT — Vault product demo stage (top-aligned with the heading) */}
          <div className="lg:col-span-5 lg:mt-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.0, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-video w-full max-w-[640px] mx-auto lg:ml-auto border border-[#0a0a0a] shadow-[0_30px_80px_-30px_rgba(10,10,10,0.35)]"
            >
              <span className="absolute -top-px -left-px w-4 h-4 border-t border-l border-[#0a0a0a]" />
              <span className="absolute -top-px -right-px w-4 h-4 border-t border-r border-[#0a0a0a]" />
              <span className="absolute -bottom-px -left-px w-4 h-4 border-b border-l border-[#0a0a0a]" />
              <span className="absolute -bottom-px -right-px w-4 h-4 border-b border-r border-[#0a0a0a]" />

              <div className="absolute inset-0 overflow-hidden bg-[#070707]">
                {VAULT_DEMO_STREAM_URL ? (
                  <iframe
                    data-testid={HERO.video}
                    src={VAULT_DEMO_STREAM_URL}
                    className="absolute inset-0 w-full h-full border-none"
                    loading="eager"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    title="Arvios Vault — product demo"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white/75 pointer-events-none">
                    <div className="inline-flex items-center justify-center h-14 w-14 border border-white/40 rounded-full">
                      <Play size={22} strokeWidth={1.4} />
                    </div>
                    <div className="mt-4 font-mono text-[12px] uppercase tracking-[0.3em]">
                      Vault &mdash; product demo
                    </div>
                  </div>
                )}

                {/* HUD chrome */}
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-white/65">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-1 w-1 bg-emerald-400 nd-blink" />
                    LIVE
                  </span>
                  <span>ARVIOS &middot; VAULT</span>
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-white/65">
                  <span>Records &middot; Reports &middot; Ally</span>
                  <span>ARV-0001</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
